import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhoneIcon from '../../Media/PhoneIcon';
import { ConfigureTenent, getAvailableNumber } from '../../Redux/Actions/Admin';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import {
  createInternalSettings,
  deletePhoneNumberApi,
  getInternalSettings,
  getTenantDetails,
  updateClient,
  updateClientEmailSettings,
  updateRestrictCommunication
} from '../../Redux/Actions/SelfServe';
import { ToastClearAction } from '../../Redux/Actions/Toast';
import { hideSuccessMessage } from '../../Redux/Reducer/SelfServe';
import { SETTINGS_DATA } from './constants';
import EmailDeletionPopup from './EmailDeletionPopup';
import { formatPhoneNumber } from './utils';
import CommunityPhoneModel from './communityPhoneModel';

const popupState = {
  phoneDelete: {
    msg: 'Deleting Phone Number!',
    subText: '',
    icon: null,
    active: true
  },
  emailDelete: {
    msg: 'Deleting Email!',
    subText: '',
    icon: null,
    active: false
  },
  phoneAdd: {
    msg: 'Purchasing new phone number!',
    subText: '',
    icon: PhoneIcon,
    active: false,
    btnReverse: true
  },
  phoneDeleted: {
    msg: 'Phone number deleted!',
    subText: '',
    icon: () => (
      <DeleteForeverIcon sx={{ fontSize: 48, color: 'error.main' }} />
    ),
    active: false
  }
};

const label = `Restrict replies to "non-Kelsey" leads`;
const gptLabel = `Allow Kelsey to generate tour notes`;

const iconColor = 'text.secondary';

const checkboxLabelSx = {
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '17px',
    letterSpacing: '0.04em',
    color: 'text.primary'
  }
};

const switchLabelSx = {
  '& .MuiTypography-root': {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '19px',
    color: 'text.secondary'
  }
};

const Settings = ({ tenantId }) => {
  const [settingsList, setSettingsList] = useState(SETTINGS_DATA);
  const [phoneNum, setPhoneNum] = useState('');
  const [deletePhone, setDeletePhone] = useState('emailDelete');
  const [popUpStates, setpopUpStates] = useState(popupState);

  const [popup, setPopup] = useState(false);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const tenantData = useSelector(state => state.SelfServe);
  const tenant_id = tenantData?.userData?.data?._id;
  // Save internal settings immediately when any value changes
  const saveInternalSettings = (updatedList, newBotStatus) => {
    const dataObj = {};
    updatedList.forEach(item => {
      dataObj[item.key] = item.checked;
    });
    dataObj.enableBot = newBotStatus;
    if (tenantId) {
      dataObj.clientId = tenantId;
    }
    dispatch(createInternalSettings({ payload: dataObj }));
  };

  const saveRestrictAndGPT = (newKelseyOwned, newGptEnabled) => {
    dispatch(
      updateRestrictCommunication({
        tenantId: tenant_id,
        restrictCommunication: {
          kelsey: newKelseyOwned,
          houseAccount: false,
          knockAgent: false
        },
        isGPTEnable: newGptEnabled
      })
    );
  };

  const handleChange = (event, item, index) => {
    const updatedSettings = settingsList.map((settingsItem, i) => {
      if (i === index) {
        settingsItem.checked = event.target.checked;
      }
      return settingsItem;
    });
    setSettingsList(updatedSettings);
    saveInternalSettings(updatedSettings, botStatus);
  };
  const handleCheckboxChange = event => {
    const newValue = event.target.checked;
    setKelseyOwned(newValue);
    saveRestrictAndGPT(newValue, gptEnabled);
  };

  const handleGPTChange = event => {
    const newValue = event.target.checked;
    setGPTEnabled(newValue);
    saveRestrictAndGPT(kelseyOwned, newValue);
  };
  const settingsListData = useSelector(state => state.SelfServe.settingsList);
  const toast = useSelector(state => state.Toast);
  const selfServe = useSelector(state => state.SelfServe);
  const phoneNumbers = useSelector(state => state.Admin?.availableNumbers);
  const clientName = selfServe?.userData?.data?.name;
  const clientPhoneNumber = selfServe?.userData?.data?.phone;

  const restrictCommunication =
    selfServe?.userData?.data?.restrictCommunication;
  const isGPTEnabled = selfServe?.userData?.data?.isGPTEnable;
  const isLiveFromStore = selfServe?.userData?.data?.isLive;
  const [isLive, setIsLive] = useState(isLiveFromStore || false);
  const enableRentCafeCRMFromStore =
    selfServe?.userData?.data?.enableRentCafeCRM;
  const [enableRentCafeCRM, setEnableRentCafeCRM] = useState(
    enableRentCafeCRMFromStore || false
  );
  const pmsName = selfServe?.userData?.data?.integrationSettings?.pms;
  const enableKnockCRM = selfServe?.userData?.data?.enableKnockCRM;
  const { userData: currentUser } = useContext(GlobalContext);
  const isSuperAdmin = currentUser?.role?.name === 'SUPER_ADMIN';
  const showRentCafeCRMToggle =
    isSuperAdmin && pmsName === 'yardi' && enableKnockCRM === true;
  const [kelseyOwned, setKelseyOwned] = useState(
    restrictCommunication?.kelsey || false
  );
  const [gptEnabled, setGPTEnabled] = useState(isGPTEnabled || false);

  useEffect(() => {
    setIsLive(isLiveFromStore || false);
  }, [isLiveFromStore]);
  useEffect(() => {
    setEnableRentCafeCRM(enableRentCafeCRMFromStore || false);
  }, [enableRentCafeCRMFromStore]);
  useEffect(() => {
    setGPTEnabled(isGPTEnabled ?? false);
  }, [isGPTEnabled]);
  useEffect(() => {
    setKelseyOwned(restrictCommunication?.kelsey ?? false);
  }, [restrictCommunication]);

  const adminData = useSelector(state => state.Admin);
  const callTenantApi = () => {
    if (adminData?.users?.data) {
      const params = tenantId || adminData.users?.data?.[0]?.tenantId?.[0];
      dispatch(getTenantDetails(params));
      setDeletePhone('phoneDeleted');
      setPopup(true);
    }
  };

  useEffect(() => {
    if (adminData?.users?.data || tenantId) {
      dispatch(getTenantDetails(tenantId));
    }
  }, [adminData?.users, tenantId]);

  useEffect(() => {
    const UPDATE_SETTINGS_LIST = SETTINGS_DATA.map(item => {
      item.checked = settingsListData[item.key];
      return item;
    });
    setSettingsList(UPDATE_SETTINGS_LIST);
  }, [settingsListData]);
  const { actions, showSuccess } = useSelector(state => state.SelfServe);

  useEffect(() => {
    setOpen(showSuccess);
    setTimeout(() => dispatch(hideSuccessMessage), 10000);
  }, [showSuccess]);
  useEffect(() => {
    const params = tenantId ? tenantId : '';
    dispatch(getInternalSettings(params));
  }, []);
  const [channelEmail, setChannelEmail] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(!!clientPhoneNumber);
  const [botStatus, setBotStatus] = useState(
    settingsListData?.enableBot ?? false
  );
  const [dropDown, showDropDown] = useState(false);
  const [status, setStatus] = useState('');
  useEffect(() => {
    setBotStatus(settingsListData?.enableBot ?? false);
  }, [settingsListData]);
  useEffect(() => {
    const clientEmail = selfServe?.userData?.data?.clientEmail;
    const outgoingEmailEnabled =
      selfServe?.userData?.data?.outgoingEmailEnabled;
    if (clientEmail) {
      setChannelEmail(clientEmail);
      setEmailChecked(outgoingEmailEnabled !== false);
    }
  }, [
    selfServe?.userData?.data?.clientEmail,
    selfServe?.userData?.data?.outgoingEmailEnabled
  ]);

  useEffect(() => {
    if (tenantData?.nonOwnerKelsey) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  const selectPhn = val => {
    setPhoneNum(val);
    setAreaCode(val);
    showDropDown(false);
  };

  const [signatureOpen, setSignatureModalOpen] = useState(false);
  const [confirmPurchase, setConfirmPurchase] = useState(false);

  const handleClose = () => {
    setPhoneOpen(!phoneOpen);
    setStatus('');
    setConfirmPurchase(false);
  };

  const handleChannelAccountEmail = e => {
    dispatch(
      updateClientEmailSettings({
        clientId: tenant_id,
        payload: { clientEmail: channelEmail + '@communications.getzuma.com' }
      })
    );
  };

  const handlePhoneNumberSave = () => {
    let obj = {
      tenantName: clientName,
      phone: phoneNum,
      tenantId
    };
    dispatch(ConfigureTenent(obj));
  };

  const handleDeleteChannelAccountEmail = () => {
    if (deletePhone === 'phoneDelete') {
      const payload = {
        tenantName: clientName
      };
      setPopup(false);
      setAreaCode('');
      dispatch(deletePhoneNumberApi({ payload, callTenantApi }));
      return;
    }
    if (deletePhone === 'phoneAdd') {
      setPopup(false);
      handlePhoneNumberSave();
      return;
    }
    dispatch(
      updateClientEmailSettings({
        clientId: tenant_id,
        payload: { clientEmail: '' }
      })
    );
    setPopup(false);
    setChannelEmail('');
    setEmailChecked(false);
  };

  const handleChannelAccountEmailInput = e => {
    setChannelEmail(e.target.value);
  };

  const handleIsLiveToggle = e => {
    const newValue = e.target.checked;
    setIsLive(newValue);
    dispatch(
      updateClient({
        clientId: tenantId,
        data: { is_live: newValue }
      })
    );
  };

  const handleEnableRentCafeCRMToggle = e => {
    const newValue = e.target.checked;
    setEnableRentCafeCRM(newValue);
    dispatch(
      updateClient({
        clientId: tenantId,
        data: { enableRentCafeCRM: newValue }
      })
    );
  };

  const handleBot = e => {
    const newValue = e.target.checked;
    setBotStatus(newValue);
    saveInternalSettings(settingsList, newValue);
  };

  const handleEmailToggle = e => {
    setEmailChecked(e.target.checked);
    dispatch(
      updateClientEmailSettings({
        clientId: tenant_id,
        payload: { outgoingEmailEnabled: e.target.checked }
      })
    );
  };

  const handlePhone = e => {
    setPhoneNumber(prev => {
      const updatedSettings = settingsList.map((settingsItem, i) => {
        if (settingsItem.key === 'phoneNo') {
          settingsItem.checked = e.target.checked;
        }
        return settingsItem;
      });
      setSettingsList(updatedSettings);
      return !prev;
    });
  };
  const AreaCodeFunc = e => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length === 3) {
      dispatch(getAvailableNumber({ number: onlyNums, tenantId }));
    }
    if (onlyNums.length <= 3) {
      setAreaCode(onlyNums);
    }
  };

  const deletePhonePopup = () => {
    setDeletePhone('phoneDelete');
    setPopup(prev => !prev);
  };

  const handleDeletePop = () => {
    setDeletePhone('emailDelete');
    setPopup(true);
  };

  const phoneAddPopup = () => {
    setDeletePhone('phoneAdd');
    setPopup(true);
  };

  useEffect(() => {
    if (clientPhoneNumber) {
      setAreaCode(clientPhoneNumber);
    }
  }, [clientPhoneNumber]);
  const closeNotification = () => {
    dispatch(ToastClearAction());
  };

  const [phoneOpen, setPhoneOpen] = useState(false);
  const handleCommunityPhone = () => {
    setPhoneOpen(!open);
  };

  return (
    <Paper
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '4px',
        p: '20px',
        overflowY: 'auto'
      }}
      elevation={0}
    >
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success">Data saved successfully</Alert>
      </Snackbar>

      <EmailDeletionPopup
        handleSave={handleDeleteChannelAccountEmail}
        open={popup}
        deletePhone={deletePhone}
        data={popUpStates[deletePhone]}
        warningText={
          deletePhone
            ? 'Phoneno.  deletion warning!'
            : 'Email Deletion Warning!'
        }
        setOpen={setPopup}
      />

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px', ml: -2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                id="is_live"
                checked={isLive}
                onChange={handleIsLiveToggle}
              />
            }
            label="Live"
            labelPlacement="start"
          />
        </Box>
        {showRentCafeCRMToggle && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              sx={switchLabelSx}
              control={
                <Switch
                  id="enable_rentcafe_crm"
                  checked={enableRentCafeCRM}
                  onChange={handleEnableRentCafeCRMToggle}
                />
              }
              label="RentCafe CRM"
              labelPlacement="start"
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                id="bot_status"
                checked={botStatus}
                onChange={handleBot}
              />
            }
            label="Bot Settings"
            labelPlacement="start"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                id="email_setting"
                checked={emailChecked}
                onChange={handleEmailToggle}
              />
            }
            label="Email"
            labelPlacement="start"
          />
          {emailChecked && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: '16px' }}>
              {selfServe?.userData?.data?.clientEmail ? (
                <>
                  <Typography sx={{ mr: '8px' }}>{channelEmail}</Typography>
                  <IconButton onClick={() => handleDeletePop()}>
                    <DeleteIcon sx={{ color: iconColor }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <TextField
                    placeholder="Enter email"
                    size="small"
                    sx={{ width: '200px' }}
                    value={channelEmail}
                    onChange={handleChannelAccountEmailInput}
                  />
                  <Typography sx={{ ml: '4px', whiteSpace: 'nowrap' }}>
                    @communications.getzuma.com
                  </Typography>
                  <IconButton onClick={handleChannelAccountEmail}>
                    <SaveIcon sx={{ color: iconColor }} />
                  </IconButton>
                  <IconButton onClick={() => setChannelEmail('')}>
                    <CloseIcon sx={{ color: iconColor }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                id="phone_setting"
                checked={phoneNumber}
                onChange={handlePhone}
              />
            }
            label="Phone"
            labelPlacement="start"
          />
          {phoneNumber && (
            <Box
              onMouseOver={() => showDropDown(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: '16px',
                position: 'relative',
                '&:hover .phone-dropdown, &:focus-within .phone-dropdown': {
                  display: 'block'
                }
              }}
            >
              <TextField
                placeholder="Area code"
                size="small"
                sx={{ width: '200px' }}
                disabled={!!clientPhoneNumber}
                value={areaCode}
                onChange={AreaCodeFunc}
              />
              <Box
                className="phone-dropdown"
                sx={{
                  position: 'absolute',
                  display: dropDown ? 'block' : 'none',
                  top: '100%',
                  left: 0,
                  width: '200px',
                  zIndex: 99,
                  bgcolor: 'background.default'
                }}
              >
                {dropDown &&
                  phoneNumbers &&
                  phoneNumbers.data.map(res => (
                    <Box
                      key={res.phone}
                      onClick={() => selectPhn(res.phone)}
                      sx={{
                        p: '10px',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      {formatPhoneNumber(res.phone)}
                    </Box>
                  ))}
              </Box>
              {clientPhoneNumber ? (
                <IconButton onClick={() => deletePhonePopup()}>
                  <DeleteIcon sx={{ color: iconColor }} />
                </IconButton>
              ) : (
                <>
                  <IconButton onClick={phoneAddPopup}>
                    <SaveIcon sx={{ color: iconColor }} />
                  </IconButton>
                  <IconButton onClick={() => setAreaCode('')}>
                    <CloseIcon sx={{ color: iconColor }} />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Miscellaneous Settings */}
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: '30px' }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            color: 'text.secondary',
            fontSize: '16px',
            lineHeight: '19px',
            fontWeight: 'bold'
          }}
        >
          Miscellaneous Settings
        </Typography>
        <Box>
          <Box sx={{ display: 'flex', mt: '20px' }}>
            <FormControlLabel
              sx={checkboxLabelSx}
              onChange={handleCheckboxChange}
              control={<Checkbox checked={kelseyOwned} />}
              label={label}
            />
          </Box>
          <Box sx={{ display: 'flex', mt: '20px' }}>
            <FormControlLabel
              sx={checkboxLabelSx}
              onChange={handleGPTChange}
              control={<Checkbox checked={gptEnabled} />}
              label={gptLabel}
            />
          </Box>
          <Box
            sx={{
              width: 'fit-content',
              p: '5px',
              height: '36px',
              bgcolor: 'rgba(82, 103, 255, 0.1)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '17px',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              alignItems: 'center',
              borderRadius: '4px',
              mt: '20px',
              display: 'flex'
            }}
          >
            Community specific phone numbers
            <IconButton
              onClick={handleCommunityPhone}
              size="medium"
              sx={{ ml: '4px' }}
            >
              <SettingsIcon sx={{ color: iconColor }} />
            </IconButton>
          </Box>
        </Box>
        <CommunityPhoneModel
          handleClose={handleClose}
          open={phoneOpen}
          tenantId={tenantId}
          status={status}
          setStatus={setStatus}
          confirmPurchase={confirmPurchase}
          setConfirmPurchase={setConfirmPurchase}
        />
      </Box>

      <Snackbar
        autoHideDuration={4000}
        open={toast.toast}
        onClose={closeNotification}
        message="Phone number Saved Successfully"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Paper>
  );
};

export default Settings;
